import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, InputAdornment, IconButton, MenuItem } from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { AppState } from "redux/store";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LeadTable from "./LeadPageTable";
import { getAllLeads } from "redux/actions";

const Lead = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const rowsPerPage = 50;

  const { lead } = useSelector<AppState, AppState["leadMaster"]>(
    ({ leadMaster }) => leadMaster
  );

  const [page, setPage] = useState(0);
  const [search, setSearchQuery] = useState<string>("");
  const [searchField, setSearchField] = useState("all");

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Date", value: "date" },
    { label: "Source", value: "source" },
    { label: "Reference", value: "reference" },
    { label: "Name", value: "customerName" },
    { label: "Mobile", value: "contactNumber" },
    { label: "Email", value: "emailId" },
    { label: "City", value: "city" },
    { label: "Area", value: "area" },
    { label: "Address", value: "address" },
    { label: "RFS", value: "rfs" },
    { label: "Distance", value: "polygonDistance" },
    { label: "Latlong", value: "latLong" },
    { label: "Remarks", value: "remarks" },
  ];

  const exportLeadsToCSV = (leadData: any[]) => {
    if (!leadData || leadData.length === 0) {
      alert("No lead data available to export.");
      return;
    }

    const sanitize = (value: any) =>
      String(value ?? "")
        .replace(/,/g, " ")
        .replace(/\r?\n|\r/g, " ");

    const headers = [
      "Date",
      "Source",
      "Reference",
      "Name",
      "Mobile",
      "Email",
      "City",
      "Area",
      "Address",
      "RFS",
      "Distance",
      "Latlong",
      "Remarks",
    ];

    const csvRows: string[] = [headers.join(",")];

    leadData.forEach((item) => {
      const row = [
        sanitize(item?.date),
        sanitize(item?.source),
        sanitize(item?.reference),
        sanitize(item?.customerName),
        sanitize(item?.contactNumber),
        sanitize(item?.emailId),
        sanitize(item?.city),
        sanitize(item?.area),
        sanitize(item?.address),
        sanitize(item?.rfs),
        sanitize(item?.polygonDistance),
        sanitize(item?.latLong),
        sanitize(item?.remarks),
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "leads.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    dispatch(getAllLeads("", 0)); 
  }, [dispatch]);

  const onSearchLead = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const filteredLead = search.trim()
  ? lead.filter((item) => {
      const searchTerm = search.trim().toLowerCase();

      if (searchField === "all") {
        return searchFields
          .filter((f) => f.value !== "all")
          .some(({ value }) => {
            if (value === "rfs") {
              const rfsValue =
                Number(item.polygonDistance) === 0
                  ? "y"
                  : Number(item.polygonDistance) < 500
                  ? "p"
                  : "n";
              return rfsValue.includes(searchTerm);
            }

            const fieldValue = item[value];
            return fieldValue?.toString().toLowerCase().includes(searchTerm);
          });
      } else {
        if (searchField === "rfs") {
          const rfsValue =
            Number(item.polygonDistance) === 0
              ? "y"
              : Number(item.polygonDistance) < 500
              ? "p"
              : "n";
          return rfsValue.includes(searchTerm);
        }

        const fieldValue = item[searchField];
        return fieldValue?.toString().toLowerCase().includes(searchTerm);
      }
    })
  : lead;


  const paginatedLeads = filteredLead.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.leadMaster"]}
            </Box>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => exportLeadsToCSV(lead)}
            >
              Export
            </Button>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              width: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchQuery("");
                setPage(0);
              }}
              sx={{ width: 150 }}
            >
              {searchFields.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => onSearchLead(e.target.value)}
              placeholder={messages["common.searchHere"] as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton onClick={() => onSearchLead("")} size="small">
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <LeadTable lead={paginatedLeads} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredLead.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Lead;
